import { useState, useRef, useEffect } from 'react'

/**
 * CameraAiChat — ИИ-ассистент для нарезки видео
 * 
 * Чат-панель справа от плеера. Пользователь описывает ситуацию (тикет WB),
 * ИИ анализирует и возвращает команды для управления плеером.
 * 
 * Props:
 *   - camera: текущая камера
 *   - cameras: список всех камер (для контекста ИИ)
 *   - onSeekArchive(date, time): перемотать архив на указанное время
 *   - onClose: закрыть чат
 */
export default function CameraAiChat({ camera, cameras = [], onSeekArchive, onClose }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: `Привет! Я помогу найти нужный момент в архиве камеры «${camera?.name || 'камера'}».\n\nОпишите ситуацию — вставьте текст тикета WB, укажите дату/время или опишите что нужно найти.`,
      actions: []
    }
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  // Прокрутка к последнему сообщению
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Фокус на поле ввода
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const sendMessage = async () => {
    const text = input.trim()
    if (!text || loading) return

    // Добавляем сообщение пользователя
    const userMsg = { role: 'user', text }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setLoading(true)

    try {
      const response = await fetch('/api/camera-ai', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          message: text,
          camera_name: camera?.name,
          cameras_list: cameras.map(c => ({ name: c.name, provider: c.provider }))
        })
      })

      const data = await response.json()

      if (data.success && data.result) {
        const r = data.result
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: r.message || 'Анализирую...',
          actions: r.actions || [],
          extracted: r.extracted || {}
        }])
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: data.message || data.error || 'Произошла ошибка. Попробуйте ещё раз.',
          actions: []
        }])
      }
    } catch (err) {
      console.error('[CameraAI] Error:', err)
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Ошибка сети. Проверьте подключение и попробуйте ещё раз.',
        actions: []
      }])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const handleAction = (action) => {
    if (action.type === 'seek_archive' && action.date && action.time) {
      onSeekArchive?.(action.date, action.time)
    }
  }

  return (
    <div className="camera-ai-chat">
      {/* Шапка */}
      <div className="camera-ai-header">
        <div className="camera-ai-header-left">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a10 10 0 0 1 10 10 10 10 0 0 1-10 10A10 10 0 0 1 2 12 10 10 0 0 1 12 2z"/>
            <circle cx="12" cy="10" r="3"/>
            <path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662"/>
          </svg>
          <span>ИИ-ассистент</span>
        </div>
        <button className="camera-ai-close" onClick={onClose} title="Закрыть чат">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Сообщения */}
      <div className="camera-ai-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`camera-ai-msg ${msg.role}`}>
            <div className="camera-ai-msg-bubble">
              <div className="camera-ai-msg-text">{msg.text}</div>
              
              {/* Извлечённые данные */}
              {msg.extracted && (msg.extracted.date || msg.extracted.operation_type) && (
                <div className="camera-ai-extracted">
                  {msg.extracted.date && <span className="ai-tag">📅 {msg.extracted.date}</span>}
                  {msg.extracted.time_from && <span className="ai-tag">🕐 {msg.extracted.time_from}{msg.extracted.time_to ? ` — ${msg.extracted.time_to}` : ''}</span>}
                  {msg.extracted.operation_type && <span className="ai-tag">📦 {msg.extracted.operation_type}</span>}
                  {msg.extracted.article && <span className="ai-tag">🏷 арт. {msg.extracted.article}</span>}
                </div>
              )}

              {/* Кнопки действий */}
              {msg.actions && msg.actions.length > 0 && (
                <div className="camera-ai-actions">
                  {msg.actions.map((action, j) => (
                    <button
                      key={j}
                      className="camera-ai-action-btn"
                      onClick={() => handleAction(action)}
                    >
                      {action.type === 'seek_archive' && '▶ '}
                      {action.type === 'export_clip' && '✂ '}
                      {action.label || 'Действие'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="camera-ai-msg assistant">
            <div className="camera-ai-msg-bubble">
              <div className="camera-ai-typing">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Ввод */}
      <div className="camera-ai-input-bar">
        <textarea
          ref={inputRef}
          className="camera-ai-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Опишите ситуацию или вставьте тикет..."
          rows={2}
          disabled={loading}
        />
        <button 
          className="camera-ai-send"
          onClick={sendMessage}
          disabled={!input.trim() || loading}
          title="Отправить"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
        </button>
      </div>
    </div>
  )
}
